import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { STANDARDS } from '../compliance/types';
import Block, { LINK_BUTTON, ROW_LINK } from './Block';
import { AdminHomeData } from './types';
import { projectPath } from './sentences';
import * as fmt from '../../lib/format';

const LABEL = Object.fromEntries(STANDARDS.map((s) => [s.id, s.label]));

// Three places a leader or an admin goes next, said in plain words.
export function LookNextBlock() {
  const links: Array<{ to: string; label: string; why: string }> = [
    { to: '/flow?tab=waiting', label: 'Tickets waiting', why: 'Where work has not moved for a while, and for how long.' },
    { to: '/effort?tab=gaps', label: 'No time on open work', why: 'Tickets in progress with nothing logged.' },
    { to: '/reports-overview', label: 'Epic reports', why: 'Every epic in the month, quarter or year.' },
  ];
  return (
    <Block title="Where to look next">
      <ul className="space-y-3">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="group block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
              <span className="flex items-center gap-1 text-sm font-medium text-blue-700 group-hover:underline">
                {l.label}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </span>
              <span className="block text-sm text-gray-600">{l.why}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Block>
  );
}

// An admin: the projects furthest from the standards, with the one that is weakest in each.
export function AttentionBlock({ attention, judged, onGiveKudos }: { attention: AdminHomeData['attention']; judged: number; onGiveKudos?: (projectKey: string) => void }) {
  return (
    <Block title="Projects furthest from the standards" className="lg:col-span-3">
      {attention.length === 0 ? (
        <p className="text-sm text-gray-600">Nothing was judged this month, so there is nothing to rank yet.</p>
      ) : (
        <>
          <p className="mb-3 text-sm text-gray-600">{`${fmt.counted(judged, 'project')} judged this month; these are the ${attention.length === 1 ? 'one' : fmt.count(attention.length)} furthest from 100%.`}</p>
          <ul className="divide-y divide-gray-100">
            {attention.map((p) => (
              <li key={p.project_key} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-3 first:pt-0 last:pb-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {p.name ?? p.project_key} <span className="font-normal text-gray-500">({p.project_key})</span>
                  </p>
                  <p className="text-sm text-gray-600">{p.sentence}</p>
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-sm tabular-nums text-gray-700">{`${LABEL[p.weakest.standard]} ${fmt.percent(p.weakest.pct)}`}</span>
                  <Link to={projectPath('admin', p.project_key)} className={ROW_LINK}>
                    See project
                  </Link>
                  {onGiveKudos && (
                    <button type="button" onClick={() => onGiveKudos(p.project_key)} aria-label={`Give kudos for ${p.name ?? p.project_key}`} className={ROW_LINK}>
                      Give kudos
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      <Link to="/compliance" className={LINK_BUTTON}>
        See every project
        <ArrowRight className="h-4 w-4" aria-hidden />
      </Link>
    </Block>
  );
}
